import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, TextField, Button, Alert, Card, CardContent } from '@mui/material';
import { LibraryBooks, AddShoppingCart } from '@mui/icons-material';
import { CartItem } from '../types/CartItem';
import { UI_TEXT } from '../constants/appConstants';
import { ScannerManager, ScannerState } from '../utils/ScannerManager';

interface ScannerInterfaceProps {
  onItemScanned: (item: CartItem) => void;
  onViewCart: () => void;
}

const ScannerInterface: React.FC<ScannerInterfaceProps> = ({ onItemScanned, onViewCart }) => {
  const scannerManagerRef = useRef<ScannerManager | null>(null);
  
  // Scanner state
  const [scannerState, setScannerState] = useState<ScannerState>({
    barcode: '',
    error: null,
    foundProduct: null,
    isProcessing: false,
    isAutoAdding: false,
    showSuccess: false,
    isAutoScanning: false,
    isSubmitting: false,
  });

  // Initialize scanner manager
  useEffect(() => {
    scannerManagerRef.current = new ScannerManager({
      onItemScanned,
      onStateChange: (newState) => {
        setScannerState(prev => ({ ...prev, ...newState }));
      }
    });

    // Setup global scan listener
    const cleanup = scannerManagerRef.current.setupGlobalScanListener();

    return () => {
      cleanup();
      scannerManagerRef.current?.destroy();
    };
  }, [onItemScanned]);

  const handleBarcodeSubmit = useCallback(async () => {
    if (!scannerManagerRef.current) return;
    
    await scannerManagerRef.current.handleManualSubmit(scannerState.barcode);
  }, [scannerState.barcode]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleBarcodeSubmit();
    }
  };

  const handleClear = () => {
    scannerManagerRef.current?.clearState();
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 2,
        gap: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: 3,
        backgroundColor: '#2C507A',
        color: '#FFFFFF',
        p: 3,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Typography 
          variant="h3" 
          sx={{
            color: '#FFFFFF',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            mb: 1
          }}
        >
          {UI_TEXT.SCANNING.TITLE}
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          mt: 2,
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
          border: '2px solid #4CAF50',
          borderRadius: '20px',
          px: 3,
          py: 1
        }}>
          <Box sx={{
            width: 12,
            height: 12,
            backgroundColor: '#4CAF50',
            borderRadius: '50%',
            mr: 1,
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': { opacity: 1, transform: 'scale(1)' },
              '50%': { opacity: 0.7, transform: 'scale(1.2)' },
              '100%': { opacity: 1, transform: 'scale(1)' }
            }
          }} />
          <Typography sx={{ 
            color: '#4CAF50', 
            fontWeight: 600, 
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Scanner Ready
          </Typography>
        </Box>
      </Box>

      {/* Scanner Area */}
      <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 4 }}>
          {!scannerState.foundProduct ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 3,
              }}
            >
              <LibraryBooks sx={{ 
                fontSize: 80, 
                color: scannerState.isProcessing || scannerState.isAutoScanning ? '#F06C38' : '#2C507A', 
                mb: 2,
                animation: scannerState.isProcessing || scannerState.isAutoScanning ? 'pulse 1s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.1)' },
                  '100%': { transform: 'scale(1)' }
                }
              }} />
              
              <Typography 
                variant="h5" 
                sx={{
                  color: scannerState.isProcessing || scannerState.isAutoScanning ? '#F06C38' : '#2C507A',
                  fontWeight: 600,
                  textAlign: 'center',
                  mb: 2
                }}
              >
                {scannerState.isAutoScanning ? 'AUTO-SCANNING...' : scannerState.isProcessing ? 'SCANNING...' : 'Manual Barcode Entry'}
              </Typography>
              
              {(scannerState.isProcessing || scannerState.isAutoScanning) && (
                <Typography 
                  variant="h6" 
                  sx={{
                    color: '#F06C38',
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: 2,
                    animation: 'blink 1s infinite',
                    '@keyframes blink': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                      '100%': { opacity: 1 }
                    }
                  }}
                >
                  🔍 {scannerState.isAutoScanning ? 'AUTO-SCANNING DETECTED...' : 'PROCESSING BARCODE...'}
                </Typography>
              )}
          
              <TextField
                fullWidth
                variant="outlined"
                label="Enter Barcode"
                value={scannerState.barcode}
                onChange={(e) => setScannerState(prev => ({ ...prev, barcode: e.target.value }))}
                onKeyPress={handleKeyPress}
                placeholder="9780262033848"
                // helperText="Try: 9780262033848 (Algorithms), OTU001 (Ridgebacks), or 9788199204775 (Way to Success)"
                sx={{
                  maxWidth: 400,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.2rem',
                    padding: '12px',
                  }
                }}
                disabled={scannerState.isProcessing}
              />
              
              <Button
                variant="contained"
                size="large"
                onClick={handleBarcodeSubmit}
                disabled={scannerState.isProcessing || !scannerState.barcode.trim()}
                startIcon={<AddShoppingCart />}
                sx={{
                  minWidth: 200,
                  py: 1.5,
                  px: 3,
                  backgroundColor: scannerState.isProcessing ? '#F06C38' : '#2C507A',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  boxShadow: '0 2px 8px rgba(44, 80, 122, 0.3)',
                  '&:hover': {
                    backgroundColor: scannerState.isProcessing ? '#E55A2B' : '#1E3A5F',
                    boxShadow: '0 4px 12px rgba(44, 80, 122, 0.4)',
                  },
                  '&:disabled': {
                    backgroundColor: '#ADB5BD',
                    color: '#FFFFFF',
                  },
                }}
              >
                {scannerState.isProcessing ? 'Scanning...' : 'Add to Cart'}
              </Button>
            </Box>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  color: '#F06C38',
                  fontWeight: 700,
                  mb: 2,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {UI_TEXT.SCANNING.PRODUCT_FOUND}
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#2C507A',
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                {scannerState.foundProduct.name}
              </Typography>
              <Typography 
                variant="h3" 
                sx={{ 
                  color: '#2C507A',
                  fontWeight: 700,
                  mb: 2,
                }}
              >
                ${scannerState.foundProduct.price.toFixed(2)}
              </Typography>
              {scannerState.foundProduct.author && (
                <Typography 
                  sx={{ 
                    color: '#6C757D',
                    mb: 2,
                    fontSize: '1rem',
                  }}
                >
                  by {scannerState.foundProduct.author}
                </Typography>
              )}
              <Typography 
                sx={{ 
                  color: '#6C757D',
                  mb: 3,
                  fontSize: '0.9rem',
                }}
              >
                {scannerState.isAutoAdding ? 'Adding to cart...' : 'Adding to cart in 2 seconds...'}
              </Typography>
              <Box sx={{ 
                width: '100%', 
                height: 4, 
                backgroundColor: '#E9ECEF', 
                borderRadius: 2,
                overflow: 'visible',
                mb: 2
              }}>
                <Box sx={{
                  width: scannerState.isAutoAdding ? '100%' : '0%',
                  height: '100%',
                  backgroundColor: '#F06C38',
                  borderRadius: 2,
                  animation: scannerState.isAutoAdding ? 'none' : 'progress 2s linear forwards',
                  '@keyframes progress': {
                    '0%': { width: '0%' },
                    '100%': { width: '100%' }
                  }
                }} />
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Success Message */}
      {scannerState.showSuccess && (
        <Alert 
          severity="success" 
          onClose={() => setScannerState(prev => ({ ...prev, showSuccess: false }))}
          sx={{ 
            mb: 2,
            fontSize: '1.2rem',
            fontWeight: 'bold',
            backgroundColor: '#e8f5e8',
            border: '2px solid #4caf50',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(76, 175, 80, 0.3)',
            '& .MuiAlert-message': {
              fontSize: '1.1rem',
              fontWeight: 'bold'
            }
          }}
        >
          ✅ Product successfully added to cart!
        </Alert>
      )}

      {/* Error Display */}
      {scannerState.error && (
        <Alert 
          severity="error" 
          onClose={() => setScannerState(prev => ({ ...prev, error: null }))}
          sx={{ 
            mb: 2,
            fontSize: '1.2rem',
            fontWeight: 'bold',
            backgroundColor: '#ffebee',
            border: '2px solid #f44336',
            borderRadius: '8px',
            boxShadow: '0 4px 8px rgba(244, 67, 54, 0.3)',
            '& .MuiAlert-message': {
              fontSize: '1.1rem',
              fontWeight: 'bold'
            }
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setScannerState(prev => ({ ...prev, error: null, barcode: '' }));
              }}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              Clear & Try Again
            </Button>
          }
        >
          {scannerState.error}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        justifyContent: 'center',
        backgroundColor: '#F8F9FA',
        p: 3,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Button
          variant="outlined"
          onClick={handleClear}
          sx={{ 
            minWidth: 150,
            py: 1.5,
            px: 3,
            borderColor: '#2C507A',
            color: '#2C507A',
            backgroundColor: '#FFFFFF',
            borderWidth: 2,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            '&:hover': {
              backgroundColor: '#2C507A',
              color: '#FFFFFF',
              borderColor: '#2C507A',
              borderWidth: 2,
            },
          }}
        >
          Clear
        </Button>
        <Button
          variant="contained"
          onClick={onViewCart}
          sx={{ 
            minWidth: 150,
            py: 1.5,
            px: 3,
            backgroundColor: '#2C507A',
            color: '#FFFFFF',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            boxShadow: '0 2px 8px rgba(44, 80, 122, 0.3)',
            '&:hover': {
              backgroundColor: '#1E3A5F',
              boxShadow: '0 4px 12px rgba(44, 80, 122, 0.4)',
            },
          }}
        >
          {UI_TEXT.SCANNING.VIEW_CART}
        </Button>
      </Box>
    </Box>
  );
};

export default ScannerInterface;